import React from "react";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import Api from "services/api";
import { formatServerDateTime } from "helpers/formatServerDateTime";

const STATUS_STYLES = {
  pending: { background: "#ffb82a33", color: "#7a4f01" },
  retrying: { background: "#fd7e1433", color: "#c05600" },
  sent: { background: "#1c7c3633", color: "#1c7c36" },
  failed: { background: "#e33a3133", color: "#b51a12" },
  superseded: { background: "#80808033", color: "#555" },
  skipped: { background: "#80808033", color: "#555" },
};

const prettyJson = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return String(value); // not JSON — show raw
  }
};

const CodeBlock = ({ title, content, emptyText }) => (
  <div style={{ marginBottom: 16 }}>
    <h2 className="main_heading2" style={{ fontSize: 15, marginBottom: 6 }}>{title}</h2>
    {content ? (
      <pre
        style={{
          background: "#0f172a",
          color: "#d1e3f0",
          borderRadius: 8,
          padding: 14,
          fontSize: 12,
          lineHeight: 1.5,
          maxHeight: 320,
          overflow: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {content}
      </pre>
    ) : (
      <p style={{ color: "#809FB8", fontSize: 13, margin: 0 }}>{emptyText}</p>
    )}
  </div>
);

/** Full request/response detail for one KCDC outbound event log row. */
const ApiLogDetailModal = ({ logId, isOpen, toggle }) => {
  const [log, setLog] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!isOpen || !logId) return;
    let active = true;
    setLog(null);
    setLoading(true);
    Api.fetchApiLogDetail(logId)
      .then((res) => {
        if (!active) return;
        if (res?.status >= 200 && res?.status < 300) {
          setLog(res?.data?.data || null);
        }
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [isOpen, logId]);

  const requestPending = log && !log.request_body && log.status === "pending";

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" scrollable>
      <ModalHeader toggle={toggle}>API Log Details</ModalHeader>
      <ModalBody>
        {loading ? (
          <p style={{ textAlign: "center", color: "#809FB8" }}>Loading…</p>
        ) : !log ? (
          <p style={{ textAlign: "center", color: "#809FB8" }}>Could not load log details.</p>
        ) : (
          <>
            <div className="table_responsive" style={{ marginBottom: 16 }}>
              <table style={{ width: "100%", fontSize: 13 }}>
                <tbody>
                  <tr>
                    <td style={{ color: "#809FB8", padding: "3px 12px 3px 0", whiteSpace: "nowrap" }}>Event Type</td>
                    <td style={{ padding: "3px 0" }}>{log.event_type}</td>
                    <td style={{ color: "#809FB8", padding: "3px 12px", whiteSpace: "nowrap" }}>Status</td>
                    <td style={{ padding: "3px 0" }}>
                      <span style={{
                        display: "inline-block", padding: "1px 10px", fontSize: 12, fontWeight: 600,
                        borderRadius: 10, textTransform: "capitalize",
                        ...(STATUS_STYLES[log.status] || STATUS_STYLES.superseded),
                      }}>
                        {log.status}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: "#809FB8", padding: "3px 12px 3px 0" }}>Job</td>
                    <td style={{ padding: "3px 0" }}>{log.appointment?.appid || log.appointment_id || "-"}</td>
                    <td style={{ color: "#809FB8", padding: "3px 12px" }}>Response Code</td>
                    <td style={{ padding: "3px 0" }}>{log.response_code ?? "-"}</td>
                  </tr>
                  <tr>
                    <td style={{ color: "#809FB8", padding: "3px 12px 3px 0" }}>Correlation ID</td>
                    <td style={{ padding: "3px 0" }}>{log.correlation_id || "-"}</td>
                    <td style={{ color: "#809FB8", padding: "3px 12px" }}>Event UUID</td>
                    <td style={{ padding: "3px 0", wordBreak: "break-all" }}>{log.event_uuid || "-"}</td>
                  </tr>
                  <tr>
                    <td style={{ color: "#809FB8", padding: "3px 12px 3px 0" }}>Created At</td>
                    <td style={{ padding: "3px 0" }} colSpan={3}>
                      {formatServerDateTime(log.created_at)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {log.error && (
              <div style={{
                background: "#e33a3115", border: "1px solid #e33a3140", color: "#b51a12",
                borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 16,
                whiteSpace: "pre-wrap", wordBreak: "break-word",
              }}>
                {log.error}
              </div>
            )}

            <CodeBlock
              title="Request Body"
              content={prettyJson(log.request_body)}
              emptyText={requestPending
                ? "Not sent yet — the request body is captured when the event is delivered after the debounce window."
                : "No request body recorded for this event."}
            />
            <CodeBlock
              title="Response Body"
              content={prettyJson(log.response_body)}
              emptyText="No response body recorded for this event."
            />
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default ApiLogDetailModal;
